// Helpers PUROS del modo "Creador de video" — ADR-2026-09-07-modo-creador-video.
//
// El modo es el asistente en video sin agente, en pantalla propia. Lo que tiene de
// lógica propia es poco —el resto lo toma de `directMode` (créditos, topes de duración)
// y de Prompt libre (referencias, "qué falta")— y vive acá para probarse sin React.

import {
  clampDuracion,
  creditsForDirectCall,
  type VideoQuality,
} from '../asistente/directMode';
import type { VideoResolution } from '../../../../utils/credits';

/** Formato con el que abre el modo: 9:16, el de un video para redes (el mismo default
 *  que Video con influencer). */
export const FORMATO_DEFAULT_VIDEO = 'story';

/** Los ajustes de video de la ruedita, juntos: son los que definen el costo. */
export interface AjustesVideo {
  durationSeconds: number;
  quality: VideoQuality;
  resolution: VideoResolution;
}

/** Con qué arranca la ruedita: profesional, 720p y **15 segundos** (decisión de producto
 *  2026-09-19: con el escritor de prompts los videos son de varios beats, y 5 s quedaba
 *  corto para una reseña; el asistente sigue en 5). */
export const AJUSTES_VIDEO_DEFAULT: AjustesVideo = {
  durationSeconds: 15,
  quality: 'profesional',
  resolution: '720p',
};

/**
 * Créditos del video, para la pastilla y el guard. Es la cuenta del modo directo del
 * asistente (`creditsForDirectCall`), que a su vez es la tarifa de `utils/credits`:
 * duración × tarifa por segundo de calidad × resolución.
 */
export function creditosCreadorVideo(ajustes: AjustesVideo): number {
  return creditsForDirectCall({
    outputKind: 'video',
    durationSeconds: ajustes.durationSeconds,
    videoQuality: ajustes.quality,
    videoResolution: ajustes.resolution,
  });
}

/**
 * Cambiar la calidad ACOTA la duración al tope de la calidad nueva (básica 15s /
 * profesional 30s): sin esto, bajar de profesional a básica con 25s dejaba un payload
 * fuera de rango. Es lo que hace `cambiarVideoQuality` en el asistente, como función.
 */
export function conCalidad(ajustes: AjustesVideo, quality: VideoQuality): AjustesVideo {
  return {
    ...ajustes,
    quality,
    durationSeconds: clampDuracion(ajustes.durationSeconds, quality),
  };
}

/** El resultado de `generateVideoFromPrompt` como ids de la tanda: uno o ninguno (el
 *  camino viejo de n8n puede no escribir fila). */
export function idsDeVideo(videoId: string | null | undefined): string[] {
  return videoId ? [String(videoId)] : [];
}

export const PLACEHOLDER_GENERAL = 'Describí el video que querés generar…';
export const PLACEHOLDER_CON_TIPO = 'Indicaciones para el video (opcional)…';

/**
 * Qué falta para poder generar, o `undefined` si nada. Reemplaza a `faltaPromptLibre`
 * en este modo: el texto es OPCIONAL, son indicaciones libres para el escritor del
 * backend (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo,
 * ADR-2026-09-19-el-idioma-es-una-direccion-no-una-linea-hablada). Alcanza con que el
 * escritor tenga ALGO de dónde partir: un tipo elegido, o al menos un adjunto, o texto.
 * Lo único que no se puede generar es la nada: General, sin adjuntos y sin texto.
 */
export function faltaCreadorVideo(args: {
  prompt: string;
  listo: boolean;
  tipoVideo: string;
  cantidadAdjuntos: number;
}): string | undefined {
  if (!args.listo) return 'Cargando tu cuenta…';
  if (args.prompt.trim()) return undefined;
  if (args.tipoVideo !== 'general') return undefined;
  if (args.cantidadAdjuntos > 0) return undefined;
  return 'Elegí un tipo de video, adjuntá algo o escribí qué querés';
}
